import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronRight, Sparkles, Plus, X } from 'lucide-react';
import { useOperations } from './useOperations';
import OperationRow from './OperationRow';
import TaskComposer from './TaskComposer';
import PlanIntake from './PlanIntake';
import DecisionComposer from './DecisionComposer';
import MiloBriefing from './MiloBriefing';
import Carousel from './Carousel';
import { MiloNotebookItems } from '../_shared/MiloNotebookItems';
import { isIntelligenceNotebookEnabled } from '../intelligence-notebook/intelligenceNotebookFlag';
import {
  getLivingBrief, getSettings, getEventConfig, planIntake, planGenerate, planGapAction, assistOperation,
  getIntelligence, getImpact, listDecisions, createDecision, deleteDecision, LOGO_URL,
  getMiloBriefing, dismissMiloNudge, setMiloQuiet, createCalendarEvent, listCalendarEvents, getDashboardStats,
} from '../../../data/api';
import {
  groupOperations, greetingWord, weekdayName, buildTimeline, currentWindow,
} from './erHelpers';
import './orchestration.css';

// Normalize a title for cross-surface de-dup (one item, one place on the page).
const _norm = (s) => String(s || '').trim().toLowerCase().replace(/\s+/g, ' ');

/*
 * EventReadinessHome — the orchestration surface (white / modern-luxury).
 *
 * One screen, one source of truth: a time-aware greeting, an explainable
 * readiness ring, the assistant command bar, a timeline strip derived from the
 * event date, and the real operations list grouped into Do next / Coming up /
 * Looks already handled / Waiting / Done. Manual task CRUD is first-class.
 *
 * Phase 0: list + manual tasks + readiness, all from live data (no fakes).
 * Phases 1–3 layer in auto-status, intake/intelligence, and per-task actions.
 */

const RING_CIRC = 326.7; // 2π·52

function formatEventDate(iso) {
  if (!iso) return null;
  try {
    return new Date(String(iso).slice(0, 10) + 'T00:00:00').toLocaleDateString('en-US', {
      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
    });
  } catch { return null; }
}

function locationLabel(loc) {
  if (!loc) return null;
  if (typeof loc === 'string') return loc;
  return loc.venue_name || loc.city || loc.free_text ||
    [loc.city, loc.country].filter(Boolean).join(', ') || null;
}

export default function EventReadinessHome({ intent: intentProp = null, onNavigateToModule = null, focusEntity = null }) {
  const { operations, loading, busy, error, actions } = useOperations();
  const [intent, setIntent] = useState(intentProp);
  useEffect(() => { if (intentProp) setIntent(intentProp); }, [intentProp]);
  const [brief, setBrief] = useState(null);
  const [intel, setIntel] = useState(null);
  const [settings, setSettings] = useState(null);
  const [composer, setComposer] = useState({ open: false, mode: 'add', op: null });
  const [showDone, setShowDone] = useState(false);
  const [showAllContrib, setShowAllContrib] = useState(false);
  const [cmd, setCmd] = useState('');
  const [logoOk, setLogoOk] = useState(true);
  const [intakeOpen, setIntakeOpen] = useState(false);
  const [planBusy, setPlanBusy] = useState(false);
  const [focusOpId, setFocusOpId] = useState(null);
  const [impactLever, setImpactLever] = useState('guests');
  const [impactDelta, setImpactDelta] = useState(20);
  const [impact, setImpact] = useState(null);
  const [decisions, setDecisions] = useState([]);
  const [decisionOpen, setDecisionOpen] = useState(false);
  // Locally-hidden "since your last visit" changes. These are derived diffs with
  // no persisted dismiss endpoint, so dismissal is session-local only.
  const [hiddenSinceKeys, setHiddenSinceKeys] = useState(() => new Set());
  // Risks the host dismissed this session (e.g. a wrong "Catering not booked").
  // Session-local hide so a false notice can always be cleared; reappears only
  // if the backend still computes it next visit (until the root cause is fixed).
  const [hiddenRiskKeys, setHiddenRiskKeys] = useState(() => new Set());
  const cmdRef = useRef(null);

  const reloadBrief = useCallback(async () => {
    try { setBrief(await getLivingBrief()); } catch { /* readiness falls back to client derivation */ }
  }, []);
  const reloadIntel = useCallback(async () => {
    try { setIntel(await getIntelligence()); } catch { /* command-center sections just hide */ }
  }, []);
  const [briefing, setBriefing] = useState(null);
  const reloadBriefing = useCallback(async () => {
    try { setBriefing(await getMiloBriefing()); } catch { setBriefing(null); }
  }, []);

  useEffect(() => { reloadBrief(); reloadIntel(); }, [reloadBrief, reloadIntel]);
  // Event identity (host name/salutation, honoree, event name, date) lives in
  // /event/config — NOT /settings (which only carries bcc + auto-reply template).
  // Merge both so the greeting + brand title + event date all resolve. event/config
  // names its title field `name`, which we surface as `event_name` for the header.
  const loadSettings = useCallback(async () => {
    const [base, cfg] = await Promise.all([
      getSettings().catch(() => ({})),
      getEventConfig().catch(() => ({})),
    ]);
    setSettings({ ...(base || {}), ...(cfg || {}), event_name: (cfg && cfg.name) || base?.event_name || '' });
  }, []);
  useEffect(() => { loadSettings(); }, [loadSettings]);
  // Live invited count for the header — the guest LIST is the truth ("85 guests invited" was
  // the Plan-Intake estimate typed during setup, not the real list). Falls back to the estimate.
  const [liveInvited, setLiveInvited] = useState(null);
  useEffect(() => { (async () => {
    try { const s = await getDashboardStats();
      const n = s?.guest_statistics?.rsvp_status?.total_invited;
      if (Number.isFinite(n) && n > 0) setLiveInvited(n);
    } catch { /* header falls back to the intake estimate */ }
  })(); }, []);
  // Readiness + intelligence + Milo's briefing derive from operations; refresh when they change.
  const opsSig = operations.map((o) => `${o._id}:${o.state}`).join(',');
  useEffect(() => { reloadBrief(); reloadIntel(); reloadBriefing(); }, [opsSig, reloadBrief, reloadIntel, reloadBriefing]);

  const handleDismissNudge = useCallback(async (key) => {
    setBriefing((b) => (b ? { ...b, nudges: (b.nudges || []).filter((n) => n.key !== key) } : b));
    try { await dismissMiloNudge(key); } catch { /* best-effort; refresh restores truth */ }
    reloadBriefing();
  }, [reloadBriefing]);

  const handleSetQuiet = useCallback(async (q) => {
    setBriefing((b) => (b ? { ...b, quiet: q, nudges: q ? [] : b.nudges } : b));
    try { await setMiloQuiet(q); } catch { /* best-effort; refresh restores truth */ }
    reloadBriefing();
  }, [reloadBriefing]);
  // Impact what-if recomputes whenever the lever or delta changes.
  useEffect(() => {
    let cancel = false;
    getImpact(impactLever, impactDelta).then((r) => { if (!cancel) setImpact(r); }).catch(() => {});
    return () => { cancel = true; };
  }, [impactLever, impactDelta]);
  const reloadDecisions = useCallback(async () => {
    try { setDecisions((await listDecisions()).decisions || []); } catch { /* section hides */ }
  }, []);
  useEffect(() => { reloadDecisions(); }, [reloadDecisions]);

  const todayIso = new Date().toISOString().slice(0, 10);
  const groups = useMemo(() => groupOperations(operations), [operations]);
  // De-dup: any task already surfaced in "things that need your attention"
  // (a nudge whose action points at an op) must NOT also appear in the To Do
  // List below — that double-listing is what made "Open task" feel like it
  // jumped to a duplicate. We strip those op_ids from the open groups only;
  // counts, readiness and the briefing itself are untouched.
  const attentionOpIds = useMemo(() => {
    const s = new Set();
    if (!briefing?.quiet) {
      (briefing?.nudges || []).forEach((n) => (n.actions || []).forEach((a) => { if (a?.op_id) s.add(a.op_id); }));
    }
    return s;
  }, [briefing]);
  const visibleGroups = useMemo(() => {
    if (!attentionOpIds.size) return groups;
    const strip = (arr) => (arr || []).filter((o) => !attentionOpIds.has(o._id));
    return {
      ...groups,
      doNext: strip(groups.doNext),
      comingUp: strip(groups.comingUp),
      waiting: strip(groups.waiting),
      handled: strip(groups.handled),
    };
  }, [groups, attentionOpIds]);
  // Memoized so its identity is stable across renders (it feeds noticeRegistry).
  const verdict = brief?.plan?.timeline_verdict || null;
  const hasPlanTasks = operations.some((o) => String(o.signal_key || '').startsWith('plan:'));

  // Cross-surface de-dup: one item should appear in exactly ONE place. The To Do
  // list (operations) is canonical; an item already on it — or already promoted
  // into "needs your attention" — must NOT also show as a recommendation, likely
  // gap, or risk. Priority: To Do > attention > recommendation > gap > risk.
  const nudgeHeadlines = useMemo(() => {
    const s = new Set();
    if (!briefing?.quiet) (briefing?.nudges || []).forEach((n) => { if (n.headline) s.add(_norm(n.headline)); });
    return s;
  }, [briefing]);
  const claimedTitles = useMemo(() => {
    const s = new Set(nudgeHeadlines);
    (operations || []).forEach((o) => s.add(_norm(o.title)));
    return s;
  }, [operations, nudgeHeadlines]);
  const recs = useMemo(
    () => (intel?.recommendations || []).filter((r) => !claimedTitles.has(_norm(r.title))),
    [intel, claimedTitles]);
  const gaps = useMemo(() => {
    const recTitles = new Set(recs.map((r) => _norm(r.title)));
    return (brief?.plan?.gaps || []).filter((g) => !claimedTitles.has(_norm(g.title)) && !recTitles.has(_norm(g.title)));
  }, [brief, claimedTitles, recs]);
  // Titles already shown anywhere above the risk register — so risks don't repeat them.
  const advisoryClaimed = useMemo(() => {
    const s = new Set(claimedTitles);
    recs.forEach((r) => s.add(_norm(r.title)));
    gaps.forEach((g) => s.add(_norm(g.title)));
    return s;
  }, [claimedTitles, recs, gaps]);
  const since = intel?.since_last_visit || null;
  // "Since your last visit": drop self-action notices about messages the host
  // sent (no value — they sent it; the outbox already records it), plus any the
  // host hid this session.
  const sinceChanges = useMemo(() => {
    const raw = since?.changes || [];
    const isSelfMessage = (t) => /\bmessages?\b/i.test(t) && /\b(went out|sent|delivered|emailed|texted)\b/i.test(t);
    return raw
      .map((c, i) => ({ ...c, _key: `${c.icon || 'x'}:${c.text || i}` }))
      .filter((c) => !hiddenSinceKeys.has(c._key) && !isSelfMessage(c.text || ''));
  }, [since, hiddenSinceKeys]);
  const areas = intel?.readiness_by_area || [];
  const risks = intel?.risks || { critical: [], warnings: [] };
  // Risk Register shows true potential problems only. "overdue:" risks are just
  // actionable tasks already in the To Do list — drop them here to avoid a dupe.
  const isDoNextDuplicate = (r) => typeof r?.key === 'string' && r.key.startsWith('overdue:');
  const riskShown = (r) => !isDoNextDuplicate(r) && !advisoryClaimed.has(_norm(r.title)) && !hiddenRiskKeys.has(r.key);
  const registerCritical = (risks.critical || []).filter(riskShown);
  const registerWarnings = (risks.warnings || []).filter(riskShown);
  const allRisks = [...registerCritical, ...registerWarnings];
  const forecast = intel?.forecast || null;

  // Module → Event Readiness focus: when a module page's orchestration
  // indicator is tapped, AdminPage sets focusEntity = { op_id }. Scroll to and
  // halo the matching task (expanding Done so completed items are reachable).
  useEffect(() => {
    const opId = focusEntity?.op_id;
    if (!opId) return undefined;
    setFocusOpId(opId);
    setShowDone(true);
    const t = setTimeout(() => {
      const el = document.querySelector(`.ero [data-op-id="${opId}"]`);
      if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 90);
    const clear = setTimeout(() => setFocusOpId(null), 1800);
    return () => { clearTimeout(t); clearTimeout(clear); };
  }, [focusEntity]);

  // ---- greeting + event identity ----
  const hostName = (() => {
    const sal = String(settings?.host_salutation || '').trim();
    const name = String(settings?.host_name || settings?.honoree || '').trim();
    if (!name) return '';
    if (sal) {
      const surname = name.replace(/\s+(family|household|residence|home)\.?$/i, '').trim().split(/\s+/).filter(Boolean).slice(-1)[0] || name;
      return `${sal} ${surname}`;
    }
    const parts = name.split(/\s+/).filter(Boolean);
    return parts.length > 1 ? parts[0] : name;
  })();
  const greeting = greetingWord();
  const eventDate = intent?.event_date || (settings?.date && /^\d{4}-\d{2}-\d{2}/.test(settings.date) ? settings.date : null);
  const eventDateLabel = formatEventDate(eventDate) || (settings?.date || null);
  const place = locationLabel(intent?.location);
  const guests = liveInvited ?? intent?.estimated_guests;
  const subParts = [eventDateLabel, place, guests ? `${guests} guests invited` : null].filter(Boolean);

  // ---- readiness (server-derived score; contributors from live ops) ----
  const readiness = brief?.readiness || null;
  const score = readiness?.score ?? null;
  const ringOffset = score == null ? RING_CIRC : RING_CIRC * (1 - score / 100);

  const contributors = useMemo(() => {
    const done = groups.done.map((o) => ({ done: true, title: o.title }));
    const open = [...groups.doNext, ...groups.waiting, ...groups.comingUp, ...groups.handled]
      .map((o) => ({ done: false, title: o.title }));
    return { done, open };
  }, [groups]);

  const contribRows = useMemo(() => {
    if (showAllContrib) return [...contributors.done, ...contributors.open];
    return [...contributors.done.slice(0, 3), ...contributors.open.slice(0, 1)];
  }, [contributors, showAllContrib]);
  const hiddenOutstanding = Math.max(0, contributors.open.length - (showAllContrib ? contributors.open.length : 1));

  // ---- operational headline (honest, derived) ----
  const headline = useMemo(() => {
    if (!readiness || readiness.total === 0) return { lead: 'Let’s build your plan', rest: ' — add what you need or ask the assistant.' };
    if (readiness.overdue > 0) return { lead: `${readiness.overdue} thing${readiness.overdue > 1 ? 's' : ''} need attention now`, rest: ' — they’re past due.' };
    if (readiness.today > 0) return { lead: `${readiness.today} thing${readiness.today > 1 ? 's' : ''} need you today`, rest: groups.waiting.length ? `, ${groups.waiting.length} waiting on others.` : '.' };
    if (readiness.open > 0) return { lead: `${readiness.open} open item${readiness.open > 1 ? 's' : ''}`, rest: ', nothing overdue — you’re in good shape.' };
    return { lead: 'You’re all caught up', rest: ' — nothing pressing right now.' };
  }, [readiness, groups.waiting.length]);

  // ---- timeline ----
  // Pull the host's own manual Calendar entries (e.g. "send menu selections to
  // caterer") so the timeline reflects the SAME dates as the Calendar, not just
  // to-dos. Best-effort; a calendar hiccup leaves the to-do pins intact.
  const [calEvents, setCalEvents] = useState([]);
  useEffect(() => {
    let alive = true;
    listCalendarEvents('manual').then((rows) => { if (alive) setCalEvents(Array.isArray(rows) ? rows : []); }).catch(() => {});
    return () => { alive = false; };
  }, []);
  // The host's own dated to-dos AND calendar entries become pins on "the road to
  // your event" so the timeline reflects their real plan, de-duped by title+date.
  const timelinePins = useMemo(() => {
    const pins = [];
    (operations || [])
      .filter((o) => o.due_date && o.state !== 'completed' && o.state !== 'snoozed')
      .forEach((o) => pins.push({ title: o.title, date: String(o.due_date).slice(0, 10), kind: 'task' }));
    (calEvents || [])
      .filter((e) => e.date && e.event_type !== 'unavailable')
      .forEach((e) => pins.push({ title: e.title, date: String(e.date).slice(0, 10), kind: 'calendar' }));
    const seen = new Set();
    return pins.filter((p) => { const k = `${_norm(p.title)}|${p.date}`; if (seen.has(k)) return false; seen.add(k); return true; });
  }, [operations, calEvents]);
  const timeline = useMemo(() => buildTimeline(eventDate, timelinePins), [eventDate, timelinePins]);

  // ---- handlers ----
  const openAdd = () => setComposer({ open: true, mode: 'add', op: null });
  const openEdit = (op) => setComposer({ open: true, mode: 'edit', op });
  const closeComposer = () => setComposer((c) => ({ ...c, open: false }));

  const submitComposer = async (payload) => {
    // `add_to_calendar` is a composer-only flag — peel it off before the task save.
    const { add_to_calendar: alsoCalendar, ...taskPayload } = payload || {};
    if (composer.mode === 'edit' && composer.op) {
      await actions.update(composer.op._id, taskPayload);
    } else {
      await actions.create(taskPayload);
      // Mirror the new to-do onto the Calendar so a dated task also lands on the
      // timeline/calendar. Best-effort: a calendar hiccup must not lose the task.
      if (alsoCalendar && taskPayload.due_date) {
        try {
          await createCalendarEvent({
            title: taskPayload.title,
            date: String(taskPayload.due_date).slice(0, 10),
            event_type: 'schedule',
            note: taskPayload.detail || null,
            source: 'manual',
          });
        } catch (_) { /* task already saved; calendar mirror is non-blocking */ }
      }
    }
    closeComposer();
  };

  const toggleComplete = (op) => {
    if (op.state === 'completed') return actions.update(op._id, { state: 'active' });
    return actions.complete(op._id);
  };
  const handleAccept = (op) => actions.accept(op._id);
  const handleDismiss = (op) => actions.dismiss(op._id);
  const handleSnooze = (op) => actions.snooze(op._id);
  const handleMarkWaiting = (op) => actions.update(op._id, { state: 'waiting' });
  const handleReopen = (op) => actions.update(op._id, { state: 'active' });
  const handleArchive = (op) => actions.remove(op._id);

  // ---- plan intelligence (intake / generate / gaps) ----
  const refreshAll = useCallback(async () => { await actions.refresh(); await reloadBrief(); await reloadIntel(); }, [actions, reloadBrief, reloadIntel]);
  const submitIntake = async (payload) => {
    setPlanBusy(true);
    try {
      const res = await planIntake(payload);
      if (res?.intent) setIntent(res.intent);
      loadSettings();
      await refreshAll();
      setIntakeOpen(false);
    } finally { setPlanBusy(false); }
  };
  const generatePlan = async () => {
    setPlanBusy(true);
    try { await planGenerate(); await refreshAll(); } finally { setPlanBusy(false); }
  };
  const handleGap = useCallback(async (gap, action) => {
    setPlanBusy(true);
    try { await planGapAction(gap.signal_key, action); await refreshAll(); } finally { setPlanBusy(false); }
  }, [refreshAll]);
  // Dismiss a single "since your last visit" change. No persisted endpoint
  // exists for these derived diffs, so this is session-local only (the card
  // reappears on the next backend recompute). Flagged for backend support.
  const handleDismissRisk = useCallback((key) => {
    if (!key) return;
    setHiddenRiskKeys((prev) => { const next = new Set(prev); next.add(key); return next; });
  }, []);
  const handleDismissSince = useCallback((key) => {
    if (!key) return;
    setHiddenSinceKeys((prev) => { const next = new Set(prev); next.add(key); return next; });
  }, []);


  // Per-task assist: prepare a prefill, then hand off to the real module form
  // (reuses the Milo prefill carriers; the host reviews & sends — AI never does).
  const handleAssist = async (op) => {
    setPlanBusy(true);
    try {
      const res = await assistOperation(op._id);
      const p = res?.proposal;
      if (!p) return;
      const target = p.target || {};
      if (target.kind === 'messaging_email_template_prefill') {
        try {
          sessionStorage.setItem('milo_email_template_prefill', JSON.stringify({
            action_id: p.action_id, proposal_id: p.proposal_id, target,
            summary: p.summary, runbook_family_id: null, runbook_version: null, issued_at: Date.now(),
          }));
        } catch { /* sessionStorage unavailable — navigation still happens */ }
        onNavigateToModule?.('messaging');
      } else if (target.kind === 'tab') {
        onNavigateToModule?.(target.tab, target.entity_id || null);
      }
    } finally { setPlanBusy(false); }
  };

  // Route a recommendation/risk action to the right existing handler — every
  // action goes somewhere useful (no dead ends).
  const focusOp = (opId) => {
    setFocusOpId(opId);
    setShowDone(true);
    setTimeout(() => {
      const el = document.querySelector(`.ero [data-op-id="${opId}"]`);
      if (el && typeof el.scrollIntoView === 'function') {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } else {
        // The row is intentionally hidden (it's the attention item itself, kept
        // out of the To Do List to avoid duplication) — open it directly.
        const op = operations.find((o) => o._id === opId);
        if (op) openEdit(op);
      }
    }, 80);
    setTimeout(() => setFocusOpId(null), 1800);
  };
  const runAction = async (a) => {
    if (!a) return;
    if (a.kind === 'module' && a.target) { onNavigateToModule?.(a.target); return; }
    if (a.kind === 'add_gap' && a.signal_key) {
      setPlanBusy(true);
      try { await planGapAction(a.signal_key, 'add'); await refreshAll(); } finally { setPlanBusy(false); }
      return;
    }
    if (a.kind === 'focus' && a.op_id) { focusOp(a.op_id); return; }
    if (a.kind === 'assist' && a.op_id) {
      const op = operations.find((o) => o._id === a.op_id);
      if (op) handleAssist(op);
    }
  };

  const submitDecision = async (payload) => {
    setPlanBusy(true);
    try { await createDecision(payload); await reloadDecisions(); setDecisionOpen(false); } finally { setPlanBusy(false); }
  };
  const removeDecision = async (id) => {
    setPlanBusy(true);
    try { await deleteDecision(id); await reloadDecisions(); } finally { setPlanBusy(false); }
  };

  const submitCmd = async (e) => {
    e.preventDefault();
    const text = cmd.trim();
    if (!text || busy) return;
    setCmd('');
    let parsed = null;
    try { const r = await actions.parse(text); parsed = r?.parsed || null; } catch { /* fall back to raw */ }
    await actions.create({
      title: parsed?.title || text,
      detail: parsed?.detail || null,
      category: parsed?.category || 'other',
      urgency: parsed?.urgency || 'this_week',
      due_date: parsed?.due_date || null,
      state: parsed?.state && parsed.state !== 'completed' ? parsed.state : 'active',
      waiting_on: parsed?.waiting_on || null,
    });
  };
  const fillCmd = (text) => { setCmd(text); cmdRef.current?.focus(); };

  const rowProps = {
    busy, todayIso,
    onToggleComplete: toggleComplete, onAccept: handleAccept, onDismiss: handleDismiss,
    onEdit: openEdit, onSnooze: handleSnooze, onMarkWaiting: handleMarkWaiting,
    onReopen: handleReopen, onArchive: handleArchive, onNavigateToModule, onAssist: handleAssist,
  };
  const renderRow = (op) => (
    <OperationRow key={op._id} op={op} haloed={op._id === focusOpId} {...rowProps} />
  );

  const renderGroup = (title, dot, items, opts = {}) => {
    if (!items.length) return null;
    return (
      <section className="ero-section" key={title}>
        <div className="ero-sec-head">
          <span className={`ero-sec-dot ${dot}`} />
          <h2>{title}</h2>
          <span className="ero-cnt">{opts.count || items.length}</span>
          {opts.aiBy && <span className="ero-ai-by">✦ {opts.aiBy}</span>}
          {opts.addAction && <button type="button" className="ero-todo-add" onClick={opts.addAction}>＋ Add to-do</button>}
        </div>
        {opts.scroll && items.length > 4 ? (
          <div className="ero-todo-scroll">
            {items.map(renderRow)}
            <div className="ero-todo-scroll-hint">Showing the top of {items.length} — scroll for the rest</div>
          </div>
        ) : (
          items.map(renderRow)
        )}
      </section>
    );
  };

  const isEmpty = !loading && !error && operations.length === 0;

  return (
    <section className="ero" data-testid="event-readiness-home">
      <div className="ero-orbs" aria-hidden="true">
        <div className="ero-orb ero-orb1" /><div className="ero-orb ero-orb2" />
        <div className="ero-orb ero-orb3" /><div className="ero-orb ero-orb4" />
      </div>

      <div className="ero-wrap">
        {/* topbar */}
        <div className="ero-topbar ero-rise" style={{ animationDelay: '.02s' }}>
          <div className="ero-brandmark">
            <div>
              <div className="ero-eyebrow">Event Readiness · Orchestration</div>
              <div className="ero-brand-title">{settings?.event_name || intent?.event_type_freetext || 'Your celebration'}</div>
              <div style={{ height: 2, background: 'linear-gradient(90deg, #D4AF37 0%, #F5D061 30%, rgba(212,175,55,0.3) 60%, transparent 100%)', marginTop: 12, marginBottom: 4, boxShadow: '0 1px 4px rgba(212,175,55,0.2)' }} />
            </div>
          </div>
          <span className="ero-pill"><span className="ero-dot-live" /> Reading live data across your modules</span>
        </div>

        {/* hero */}
        <div className="ero-hero">
          <div className="ero-glass ero-accent ero-hero-left ero-rise" style={{ animationDelay: '.06s' }}>
            <div className="ero-eyebrow">{weekdayName()} · Where things stand</div>
            <div className="ero-greeting">
              {greeting}{hostName ? <>, <span className="ero-gname">{hostName}</span></> : null}{greeting === 'Working late' ? '?' : ''}
            </div>
            <div className="ero-hl-line" />
            <div className="ero-op-headline"><b>{headline.lead}</b>{headline.rest}</div>
            {subParts.length > 0 && <div className="ero-event-sub">{subParts.join(' · ')}</div>}
          </div>

          <div className="ero-glass ero-accent ero-hero-right ero-rise" style={{ animationDelay: '.10s' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
              <div className="ero-eyebrow">Readiness</div>
              <div style={{ fontSize: 11, color: 'var(--ero-ink-4)', fontFamily: 'var(--ero-serif-2)', fontStyle: 'italic' }}>
                derived from your tasks
              </div>
            </div>
            <div className="ero-ring-row">
              <div className="ero-ring">
                <svg width="118" height="118">
                  <circle cx="59" cy="59" r="52" stroke="rgba(198,167,90,.18)" strokeWidth="7" fill="none" />
                  <circle className="ero-arc-fg" cx="59" cy="59" r="52" stroke="url(#eroGold)" strokeWidth="7" fill="none"
                    strokeLinecap="round" strokeDasharray={RING_CIRC} strokeDashoffset={ringOffset} />
                  <defs><linearGradient id="eroGold" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0" stopColor="#E6C579" /><stop offset="1" stopColor="#B89A4D" />
                  </linearGradient></defs>
                </svg>
                <div className="ero-pct"><div><b>{score == null ? '—' : score}</b><span>Percent ready</span></div></div>
              </div>
              <div className="ero-contrib">
                {contribRows.length === 0 && <div style={{ fontSize: 12.5, color: 'var(--ero-ink-3)' }}>No tasks yet — your readiness fills in as you plan.</div>}
                {contribRows.map((c, i) => (
                  <div className="ero-crow" key={i}>
                    <span className={`ero-ck ${c.done ? 'done' : 'open'}`}>{c.done ? '✓' : '!'}</span>
                    <span className="ero-txt">{c.title}</span>
                  </div>
                ))}
                {(hiddenOutstanding > 0 || showAllContrib) && (
                  <button type="button" className="ero-more" onClick={() => setShowAllContrib((v) => !v)}>
                    {showAllContrib ? 'Show less' : `+${hiddenOutstanding} outstanding · full breakdown ›`}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* readiness by area — the breakdown beside the overall score */}
        {areas.length > 0 && (
          <div className="ero-glass ero-areas ero-rise" style={{ animationDelay: '.115s' }}>
            <div className="ero-sec-head" style={{ margin: '0 0 6px' }}>
              <span className="ero-sec-dot ero-d-sage" />
              <h2 style={{ fontSize: 18 }}>Readiness by area</h2>
              <span className="ero-ai-by">✦ where readiness is gained or lost</span>
            </div>
            <div className="ero-areas-grid">
              {areas.map((a) => (
                <div className="ero-area" key={a.key}>
                  <span className="ero-area-label">{a.label}</span>
                  <span className="ero-area-track">
                    <span className={`ero-area-fill ${a.score >= 70 ? 'fill-hi' : a.score >= 40 ? 'fill-mid' : 'fill-lo'}`}
                          style={{ width: `${a.score}%` }} />
                  </span>
                  <span className="ero-area-pct">{a.score}%</span>
                </div>
              ))}
            </div>
            {!hasPlanTasks && (
              <button type="button" onClick={() => setIntakeOpen(true)}
                style={{ marginTop: 12, width: '100%', textAlign: 'left', background: 'rgba(198,167,90,0.08)', border: '1px solid rgba(198,167,90,0.28)', borderRadius: 10, padding: '10px 12px', cursor: 'pointer', fontSize: 12.5, color: '#2A2622', lineHeight: 1.5 }}>
                Your plan isn’t set up yet — these areas reflect only your live data so far. <b style={{ color: '#9C7B31' }}>Finish the quick setup</b> to populate your full plan →
              </button>
            )}
          </div>
        )}

        {/* ambient proactive Milo — what he wants you to see first */}
        {!isEmpty && (
          <MiloBriefing briefing={briefing} onAction={runAction} onDismiss={handleDismissNudge}
                        onSetQuiet={handleSetQuiet} busy={planBusy} />
        )}

        {/* command bar */}
        <form className="ero-glass ero-accent ero-cmd ero-rise" style={{ animationDelay: '.14s' }} onSubmit={submitCmd}>
          <div className="ero-cmd-label"><span className="ero-eyebrow"><Sparkles size={12} style={{ verticalAlign: '-1px' }} /> Your planning assistant</span></div>
          <div className="ero-cmd-input">
            <div className="ero-cmd-spark">✦</div>
            <input ref={cmdRef} value={cmd} onChange={(e) => setCmd(e.target.value)}
                   placeholder="Tell me what you need, or add a task — e.g. “order centerpieces by next Friday”" />
            <button type="submit" className="ero-cmd-send" disabled={busy || !cmd.trim()}>Add</button>
          </div>
          <div className="ero-cmd-chips">
            <button type="button" className="ero-chip" disabled={planBusy} onClick={hasPlanTasks ? generatePlan : () => setIntakeOpen(true)}>✦ {planBusy ? 'Working…' : hasPlanTasks ? 'Refresh my plan' : 'Set up my plan'}</button>
            <button type="button" className="ero-chip" onClick={openAdd}>+ Add a task with details</button>
            <button type="button" className="ero-chip" onClick={() => fillCmd('Order centerpieces by next Friday')}>Try an example</button>
          </div>
        </form>

        {/* Milo's recommendations — what to focus on right now */}
        {recs.length > 0 && (
          <section className="ero-section ero-rise" style={{ animationDelay: '.16s' }}>
            <div className="ero-sec-head">
              <span className="ero-sec-dot ero-d-amber" />
              <h2>Milo’s recommendations</h2>
              <span className="ero-cnt">what to focus on now</span>
              <span className="ero-ai-by">✦ from your live data — never hardcoded</span>
            </div>
            <Carousel className="ero-recs" ariaLabel="Milo's recommendations">
              {recs.map((r, i) => (
                <div className="ero-glass ero-rec" key={i}>
                  <div className="ero-rec-top">
                    <span className={`ero-rec-cat cat-${r.category}`}>{r.category}</span>
                    <span className={`ero-rec-impact imp-${r.impact}`}><span className="dot" />{r.impact} impact</span>
                  </div>
                  <div className="ero-rec-title">{r.title}</div>
                  <div className="ero-rec-reason">{r.reason}</div>
                  <div className="ero-rec-actions">
                    {(r.actions || []).map((a, j) => (
                      <button key={j} type="button" className={`ero-rec-btn${j === 0 ? ' primary' : ''}`}
                              disabled={planBusy} onClick={() => runAction(a)}>{a.label}</button>
                    ))}
                  </div>
                </div>
              ))}
            </Carousel>
          </section>
        )}

        {/* Milo Tasks from the Intelligence Notebook (read-only · gated). These ARE real
            operations (created via approved write-back) — surfaced here with provenance +
            governed undo. Notebook write-back can only CREATE tasks, never edit existing ones. */}
        {isIntelligenceNotebookEnabled() && (
          <section className="ero-section ero-rise" style={{ animationDelay: '.17s' }} data-testid="er-milo-tasks">
            <MiloNotebookItems
              kind="operation"
              title="Milo Tasks · from your Notebook"
              emptyText="No tasks created from your Notebook yet."
            />
          </section>
        )}

        {/* ───────────── informative zone — at a glance, below the actionable list ───────────── */}

        {/* since your last visit — informative recap */}
        {since && !since.first_visit && (sinceChanges.length > 0 || since.readiness_to !== since.readiness_from) && (
          <div className="ero-glass ero-since ero-rise" style={{ animationDelay: '.13s' }}>
            <span className="lbl">Since your last visit</span>
            <div className="ero-since-chips">
              {sinceChanges.length === 0
                ? <span className="ero-since-quiet">No major changes — steady as she goes.</span>
                : sinceChanges.map((c) => {
                    return (
                      <span className="ero-since-chip" key={c._key}>
                        <span className={`ic ic-${c.icon}`} />{c.text}
                        <button type="button" className="ero-since-x" title="Dismiss" aria-label="Dismiss"
                                disabled={busy || planBusy} onClick={() => handleDismissSince(c._key)}>
                          <X size={12} aria-hidden="true" />
                        </button>
                      </span>
                    );
                  })}
            </div>
            {since.readiness_to !== since.readiness_from && (
              <span className="ero-since-delta">Readiness <b className={since.readiness_to < since.readiness_from ? 'down' : ''}>{since.readiness_from}% → {since.readiness_to}%</b></span>
            )}
          </div>
        )}

        {/* risk register — potential problems, presented in the compact Looking-ahead card style */}
        {allRisks.length > 0 && (
          <section className="ero-section ero-rise" style={{ animationDelay: '.18s' }}>
            <div className="ero-sec-head">
              <span className="ero-sec-dot ero-d-clay" />
              <h2>Risk register</h2>
              <span className="ero-cnt">{registerCritical.length} critical · {registerWarnings.length} warning{registerWarnings.length !== 1 ? 's' : ''}</span>
              <span className="ero-ai-by">✦ potential problems — not tasks</span>
            </div>
            <div className="ero-forecast-grid">
              {allRisks.map((r, i) => (
                <div className="ero-glass ero-fc" key={r.key || i}>
                  <button type="button" className="ero-fc-x" title="Not applicable — dismiss"
                          aria-label="Dismiss this risk" onClick={() => handleDismissRisk(r.key)}>
                    <X size={13} aria-hidden="true" />
                  </button>
                  <div className="ero-fc-label">{r.severity === 'critical' ? 'Critical risk' : 'Watch'}</div>
                  <div className="ero-fc-value" style={{ fontSize: 17, lineHeight: 1.25 }}>{r.title}</div>
                  <div className="ero-fc-basis">{r.description}{r.impact ? ` — ${r.impact}` : ''}</div>
                  <div className="ero-fc-acts">
                    {r.action && (
                      <button type="button" className="ero-rec-btn" disabled={planBusy} onClick={() => runAction(r.action)}>{r.action.label || 'Open'}</button>
                    )}
                    <button type="button" className="ero-twhy" onClick={() => handleDismissRisk(r.key)}>Not applicable</button>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* impact analysis — what-if downstream consequences */}
        <section className="ero-section ero-rise" style={{ animationDelay: '.2s' }}>
          <div className="ero-glass ero-impact">
            <div className="ero-sec-head" style={{ margin: '0 0 4px' }}>
              <span className="ero-sec-dot ero-d-slate" />
              <h2 style={{ fontSize: 18 }}>Impact analysis</h2>
              <span className="ero-ai-by">✦ see the consequences before you decide</span>
            </div>
            <div className="ero-impact-controls">
              <div className="ero-lever">
                <button type="button" className={impactLever === 'guests' ? 'on' : ''} onClick={() => { setImpactLever('guests'); setImpactDelta(20); }}>Guest count</button>
                <button type="button" className={impactLever === 'budget' ? 'on' : ''} onClick={() => { setImpactLever('budget'); setImpactDelta(-2000); }}>Budget</button>
              </div>
              <div className="ero-stepper">
                <button type="button" aria-label="decrease" onClick={() => setImpactDelta((d) => d - (impactLever === 'guests' ? 5 : 500))}>−</button>
                <span className="val">{impact?.summary || (impactLever === 'guests'
                  ? `${impactDelta > 0 ? '+' : ''}${impactDelta} guests`
                  : `${impactDelta >= 0 ? '+' : '-'}$${Math.abs(impactDelta).toLocaleString()}`)}</span>
                <button type="button" aria-label="increase" onClick={() => setImpactDelta((d) => d + (impactLever === 'guests' ? 5 : 500))}>+</button>
              </div>
            </div>
            <div className="ero-impact-lines">
              {(impact?.lines || []).map((l, i) => (
                <span key={i} className={`ero-impact-line${l.emphasis ? ' emph' : ''}${l.module ? ' clickable' : ''}`}
                      onClick={() => l.module && onNavigateToModule?.(l.module)}>{l.label}</span>
              ))}
            </div>
            {impact?.note && <div className="ero-impact-note">{impact.note}</div>}
          </div>
        </section>

        {/* predictive intelligence — explainable forecasts */}
        {forecast && (forecast.attendance || forecast.readiness_projection) && (
          <section className="ero-section ero-rise" style={{ animationDelay: '.22s' }}>
            <div className="ero-sec-head">
              <span className="ero-sec-dot ero-d-slate" />
              <h2>Looking ahead</h2>
              <span className="ero-cnt">forecast</span>
              <span className="ero-ai-by">✦ projections with their drivers — not guesses</span>
            </div>
            <div className="ero-forecast-grid">
              {forecast.attendance && (
                <div className="ero-glass ero-fc">
                  <div className="ero-fc-label">Projected attendance</div>
                  <div className="ero-fc-value">{forecast.attendance.low}–{forecast.attendance.high}</div>
                  <div className={`ero-fc-conf conf-${forecast.attendance.confidence}`}><span className="dot" />{forecast.attendance.confidence} confidence</div>
                  <div className="ero-fc-basis">{forecast.attendance.basis}</div>
                </div>
              )}
              {forecast.readiness_projection && (
                <div className="ero-glass ero-fc">
                  <div className="ero-fc-label">Readiness by event date</div>
                  <div className="ero-fc-value">{forecast.readiness_projection.projected}%<small> from {forecast.readiness_projection.current}%</small></div>
                  <div className={`ero-fc-conf conf-${forecast.readiness_projection.confidence}`}><span className="dot" />{forecast.readiness_projection.confidence} confidence</div>
                  <div className="ero-fc-basis">{forecast.readiness_projection.basis}</div>
                </div>
              )}
              {/* 'Current pace' intentionally omitted — the same Behind/Ahead/On-track
                  verdict is shown on the Timeline below, so it isn't duplicated here. */}
              {forecast.remaining && (
                <div className="ero-glass ero-fc">
                  <div className="ero-fc-label">Remaining to finish</div>
                  <div className="ero-fc-value">{forecast.remaining.projected_incomplete}<small> at risk · {forecast.remaining.open_now} open</small></div>
                  <div className="ero-fc-basis">{forecast.remaining.basis}</div>
                </div>
              )}
            </div>
          </section>
        )}

        {/* timeline */}
        {timeline && (
          <div className="ero-glass ero-timeline ero-rise" style={{ animationDelay: '.18s' }}>
            <div className="ero-tl-head">
              <span className="ero-eyebrow">Timeline — what’s due when</span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                {verdict && (
                  <span className={`ero-tl-verdict ${verdict.verdict === 'behind' ? 'behind' : verdict.verdict === 'ahead' ? 'ahead' : 'ontrack'}`}>
                    {verdict.verdict === 'behind' ? '⚑ Behind' : verdict.verdict === 'ahead' ? '✦ Ahead' : '✓ On track'} — {verdict.reason}
                  </span>
                )}
                <span style={{ fontSize: 12, color: 'var(--ero-ink-3)', fontFamily: 'var(--ero-serif-2)', fontStyle: 'italic' }}>
                  {timeline.daysOut > 0 ? `Now · ${timeline.daysOut} days out` : timeline.daysOut === 0 ? 'Today is the day' : 'Event has passed'}
                </span>
              </span>
            </div>
            <div className="ero-tl-track">
              <div className="ero-tl-fill" style={{ width: `${timeline.fillPct}%` }} />
              {timeline.marks.map((m, i) => (
                <div key={i} className={`ero-tl-mark${m.done ? ' done' : ''}`} style={{ left: `${m.pct}%` }}>
                  <span className="ero-tl-tip"><b>{m.label === 'Day of' ? 'Day of' : `${m.days} days out`}</b><span>{m.tip}</span></span>
                </div>
              ))}
              {(timeline.pins || []).slice(0, 12).map((p, i) => (
                <div key={`pin-${i}`} className="ero-tl-pin" style={{ left: `${p.pct}%` }}>
                  <span className="ero-tl-tip"><b>{p.title}</b><span>{p.kind === 'calendar' ? 'Your calendar entry' : 'Your to-do'} · in {p.daysOut} day{p.daysOut === 1 ? '' : 's'}.</span></span>
                </div>
              ))}
              <div className="ero-tl-now" style={{ left: `${timeline.nowPct}%` }}>
                <span className="ero-tl-tip"><b>You are here</b><span>{timeline.daysOut} days out · {currentWindow(timeline.daysOut)}.</span></span>
              </div>
            </div>
            {(timeline.pins || []).length > 0 && (
              <div className="ero-tl-pinnote">✦ The gold pins are your own dated to-dos and calendar entries — {timeline.pins.length} on the road ahead.</div>
            )}
            <div className="ero-tl-labels">
              {timeline.marks.map((m, i) => {
                const cur = timeline.nowPct >= m.pct - 8 && timeline.nowPct <= m.pct + 8;
                return <span key={i} className={cur ? 'cur' : ''}>{m.label}</span>;
              })}
            </div>
          </div>
        )}

        {/* decision register — institutional memory of choices */}
        {!loading && !error && (
          <section className="ero-section ero-rise">
            <div className="ero-sec-head">
              <span className="ero-sec-dot ero-d-ink" />
              <h2>Decision register</h2>
              <span className="ero-cnt">{decisions.length}</span>
              <button type="button" className="ero-rec-btn" style={{ marginLeft: 'auto' }} onClick={() => setDecisionOpen(true)}>
                + Record a decision
              </button>
            </div>
            {decisions.length === 0 ? (
              <div className="ero-glass ero-dec-empty">No decisions recorded yet — capture choices (and why) so they’re not re-litigated later.</div>
            ) : decisions.map((d) => (
              <div className="ero-glass ero-decision" key={d._id}>
                <div className="ero-dec-main">
                  <div className="ero-dec-top">
                    <span className="ero-dec-title">{d.decision}</span>
                    {d.selected && <span className="ero-dec-selected">{d.selected}</span>}
                  </div>
                  {([d.decided_at, d.owner].filter(Boolean).length > 0) && (
                    <div className="ero-dec-meta">{[d.decided_at, d.owner].filter(Boolean).join(' · ')}</div>
                  )}
                  {d.rationale && <div className="ero-dec-rationale">{d.rationale}</div>}
                </div>
                <button type="button" className="ero-dec-del" disabled={planBusy} onClick={() => removeDecision(d._id)}>Remove</button>
              </div>
            ))}
          </section>
        )}

        {/* states + grouped task list — kept at the BOTTOM per host preference,
            below the recommendation/insight cards. The To Do List scrolls after
            ~4 rows so a long list doesn't dominate the page. */}
        {loading && <div className="ero-statemsg" aria-busy="true">Loading your plan…</div>}
        {!loading && error && <div className="ero-statemsg err" role="alert">{error}</div>}

        {isEmpty && (
          <div className="ero-glass ero-empty ero-rise" style={{ animationDelay: '.2s' }}>
            <div className="ero-empty-mark">✦</div>
            <h3>Your plan starts here</h3>
            <p>Answer three quick questions and I’ll build a tailored starter checklist — then keep it in sync with your Guests, Vendors, Finance, and Messaging. Or add your own tasks anytime.</p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
              <button type="button" className="ero-btn primary" disabled={planBusy} onClick={() => setIntakeOpen(true)}>
                <Sparkles size={14} aria-hidden="true" /> {planBusy ? 'Building…' : 'Set up my plan'}
              </button>
              <button type="button" className="ero-btn ghost" onClick={openAdd}><Plus size={14} aria-hidden="true" /> Add a task</button>
            </div>
          </div>
        )}

        {!loading && !error && !isEmpty && (
          <>
            {renderGroup('To Do List', 'ero-d-amber', visibleGroups.doNext, { aiBy: 'ranked by dates & dependencies', addAction: openAdd, scroll: true })}

            {gaps.length > 0 && (
              <section className="ero-section">
                <div className="ero-sec-head">
                  <span className="ero-sec-dot ero-d-clay" />
                  <h2>You may be missing these</h2>
                  <span className="ero-cnt">{gaps.length} likely gap{gaps.length > 1 ? 's' : ''}</span>
                  <span className="ero-ai-by">✦ typical for your event — not on your list</span>
                </div>
                {gaps.map((g) => {
                  return (
                    <div className="ero-glass ero-task missing" key={g.signal_key}>
                      <div className="ero-tbody">
                        <div className="ero-ttop">
                          <span className="ero-ttitle">{g.title}</span>
                          <span className="ero-src suggested">AI Suggested</span>
                        </div>
                        <div className="ero-tnote"><span className="ni" aria-hidden="true">✦</span><span>{g.reason}</span></div>
                        {g.budget && (
                          <div className="ero-tnote" style={{ color: 'var(--ero-ink-3)' }}>
                            <span className="ni" aria-hidden="true" style={{ color: 'var(--ero-gold-deep)' }}>$</span>
                            <span>{g.budget}</span>
                          </div>
                        )}
                      </div>
                      <div className="ero-tactions">
                        <button type="button" className="ero-btn add" disabled={planBusy} onClick={() => handleGap(g, 'add')}>+ Add to my list</button>
                        <button type="button" className="ero-twhy" disabled={planBusy} onClick={() => handleGap(g, 'dismiss')}>Dismiss</button>
                      </div>
                    </div>
                  );
                })}
              </section>
            )}

            {renderGroup('Looks already handled — confirm?', 'ero-d-sage', visibleGroups.handled, { aiBy: 'detected from your live data' })}
            {renderGroup('Coming up', 'ero-d-slate', visibleGroups.comingUp)}
            {renderGroup('Waiting on others', 'ero-d-slate', visibleGroups.waiting)}

            {groups.done.length > 0 && (
              <div className="ero-section">
                <button type="button" className="ero-glass ero-done-fold" onClick={() => setShowDone((v) => !v)}>
                  <span className="ero-ck done" style={{ width: 19, height: 19 }}>✓</span>
                  <span><b>{groups.done.length} done</b>{!showDone && groups.done.length ? ` · ${groups.done.slice(0, 4).map((o) => o.title).join(', ')}${groups.done.length > 4 ? '…' : ''}` : ''}</span>
                  <span className="chev">{showDone ? 'Hide' : 'Show'} <ChevronRight size={13} style={{ verticalAlign: '-2px' }} /></span>
                </button>
                {showDone && (
                  <div style={{ marginTop: 10 }}>
                    {groups.done.map(renderRow)}
                  </div>
                )}
              </div>
            )}
          </>
        )}

        <div className="ero-foot">
          Everything here reads from your live Guests · Vendors · Finance · Messaging · Calendar — and nothing is sent, saved, or paid without your approval.
        </div>
      </div>

      <TaskComposer
        open={composer.open}
        mode={composer.mode}
        op={composer.op}
        busy={busy}
        onClose={closeComposer}
        onSubmit={submitComposer}
      />

      <PlanIntake
        open={intakeOpen}
        intent={intent}
        settings={settings}
        busy={planBusy}
        onClose={() => setIntakeOpen(false)}
        onSubmit={submitIntake}
      />

      <DecisionComposer
        open={decisionOpen}
        busy={planBusy}
        onClose={() => setDecisionOpen(false)}
        onSubmit={submitDecision}
      />
    </section>
  );
}
